import React from 'react';
import { z } from 'zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { toast } from 'sonner';
import { ArrowLeft, Banknote, BellRing, Check, Pause, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { SelectField } from '@/components/select-field';
import { addLedgerEntry, cancelDriverPayout, fetchDriverPayout, holdDriverPayout, payDriverPayout, releaseDriverPayout, remindDriverPayout, } from '@/services/driver-payout';
import { isInternalAdmin } from '@/lib/roles';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { errorHandler } from '@/lib/utils';
import { format } from 'date-fns';
import { DateField } from '@/components/date-field';
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/payouts/$publicId')({
    component: PayoutDetail,
    // ?addEntry=1 deep-links from the queue's row-level "Add entry" button
    // and auto-opens the AddEntryDialog on mount.
    validateSearch: z.object({
        addEntry: z.literal('1').optional(),
    }),
});
const STATE_VARIANT = {
    pending: 'outline',
    due: 'secondary',
    overdue: 'destructive',
    partially_paid: 'secondary',
    paid: 'default',
    on_hold: 'outline',
    cancelled: 'outline',
};
// Readable date rendering (e.g. "Jul 25, 2026") instead of raw ISO slices.
// date-fns is already a dependency here; keep the queue table's `formatDay`
// style so both surfaces read the same.
function formatDay(iso) {
    return format(new Date(iso), 'MMM d, yyyy');
}
function formatDayTime(iso) {
    return format(new Date(iso), 'MMM d, yyyy · HH:mm');
}
const ENTRY_KIND_TONE = {
    extra: 'text-amber-500',
    deduction: 'text-rose-500',
    advance: 'text-sky-500',
    settlement: 'text-emerald-500',
    bonus: 'text-emerald-500',
};
function PayoutDetail() {
    const { t } = useTranslation('internal');
    const { publicId } = Route.useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthenticationState();
    const canAdmin = isInternalAdmin(user);
    const { addEntry: shouldAutoOpenEntry } = Route.useSearch();
    const [payOpen, setPayOpen] = React.useState(false);
    const [entryOpen, setEntryOpen] = React.useState(shouldAutoOpenEntry === '1');
    const { data: payout, refetch, isLoading, } = useQuery({
        queryKey: ['driver-payout', publicId],
        queryFn: () => fetchDriverPayout(publicId),
    });
    const onMutationSuccess = (msg) => () => {
        toast.success(msg);
        refetch();
        queryClient.invalidateQueries({ queryKey: ['driver-payouts'] });
    };
    const { mutate: doRemind, isPending: reminding } = useMutation({
        mutationFn: () => remindDriverPayout(publicId),
        onSuccess: (data) => toast.success(data.sent ? t('payouts.toast.reminder_sent') : t('payouts.toast.reminder_skipped')),
        onError: errorHandler,
    });
    const { mutate: doHold } = useMutation({
        mutationFn: ({ reason }) => holdDriverPayout({ publicId, reason }),
        onSuccess: onMutationSuccess(t('payouts.toast.on_hold')),
        onError: errorHandler,
    });
    const { mutate: doRelease } = useMutation({
        mutationFn: () => releaseDriverPayout(publicId),
        onSuccess: onMutationSuccess(t('payouts.toast.released')),
        onError: errorHandler,
    });
    const { mutate: doCancel } = useMutation({
        mutationFn: ({ reason }) => cancelDriverPayout({ publicId, reason }),
        onSuccess: onMutationSuccess(t('payouts.toast.cancelled')),
        onError: errorHandler,
    });
    if (isLoading) {
        return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;
    }
    if (!payout) {
        return <p className="p-6 text-sm text-muted-foreground">—</p>;
    }
    const isOpen = payout.state !== 'paid' && payout.state !== 'cancelled';
    const canPayInFull = isOpen && payout.state !== 'on_hold' && payout.remaining !== '0.00';
    return (<div className="flex flex-col gap-6 p-6 max-w-3xl mx-auto w-full">
            <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/console/internal/payouts' })}>
                    <ArrowLeft className="h-4 w-4"/>
                    {t('payouts.page.back')}
                </Button>
                <Badge variant={STATE_VARIANT[payout.state]}>{td(t, `enum.payout_state.${payout.state}`)}</Badge>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>{payout.driverName}</CardTitle>
                    <CardDescription>{payout.campaignName ?? '—'}</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 md:grid-cols-2 text-sm">
                    <Detail label={t('payouts.page.label.period')} value={`${formatDay(payout.periodStart)} → ${formatDay(payout.periodEnd)}`}/>
                    <Detail label={t('payouts.page.label.due')} value={formatDay(payout.dueDate)}/>
                    <Detail label={t('payouts.page.label.base')} value={`${payout.currency} ${payout.baseAmount}`} mono/>
                    <Detail label={t('payouts.page.label.extras')} value={`${payout.currency} ${payout.extraTotal}`} mono/>
                    <Detail label={t('payouts.page.label.deductions')} value={`-${payout.currency} ${payout.deductionTotal}`} mono/>
                    <Detail label={t('payouts.page.label.total_owed')} value={`${payout.currency} ${payout.totalOwed}`} mono className="font-semibold"/>
                    <Detail label={t('payouts.page.label.paid')} value={`${payout.currency} ${payout.paidTotal}`} mono/>
                    <Detail label={t('payouts.page.label.remaining')} value={`${payout.currency} ${payout.remaining}`} mono className={payout.remaining === '0.00' ? 'text-emerald-500' : 'text-amber-500'}/>
                    {payout.receiptNumber && (<Detail label={t('payouts.page.label.receipt_number')} value={payout.receiptNumber}/>)}
                    <Detail label={t('payouts.page.label.photo')} value={payout.hasPhotoSubmission ? (<span className="text-emerald-500">{t('payouts.photo_received')}</span>) : (<Link to="/console/internal/photos/new" search={{ assignment: payout.assignmentPublicId ?? undefined }} className="text-amber-500 hover:underline">
                                    {t('payouts.photo_missing_log')}
                                </Link>)}/>
                    {payout.notes && <Detail label={t('payouts.page.label.notes')} value={payout.notes}/>}
                </CardContent>
            </Card>

            <PayoutLedgerCard payout={payout} disabled={!isOpen || payout.state === 'on_hold'} onAddEntry={() => setEntryOpen(true)}/>

            <div className="flex flex-row flex-wrap gap-2">
                {canPayInFull && (<Button onClick={() => setPayOpen(true)} className="flex-1 min-w-35">
                        <Banknote className="h-4 w-4"/>
                        {t('payouts.action.pay')}
                    </Button>)}
                {isOpen && payout.state !== 'on_hold' && (<Button variant="outline" onClick={() => doRemind()} disabled={reminding} className="flex-1 min-w-35">
                        <BellRing className="h-4 w-4"/>
                        {t('payouts.action.send_reminder')}
                    </Button>)}
                {canAdmin && isOpen && payout.state !== 'on_hold' && (<Button variant="outline" onClick={() => doHold({})} className="flex-1 min-w-35">
                        <Pause className="h-4 w-4"/>
                        {t('payouts.action.hold')}
                    </Button>)}
                {canAdmin && payout.state === 'on_hold' && (<Button variant="outline" onClick={() => doRelease()} className="flex-1 min-w-35">
                        <Check className="h-4 w-4"/>
                        {t('payouts.action.release')}
                    </Button>)}
                {canAdmin && isOpen && (<Button variant="destructive" onClick={() => doCancel({})} className="flex-1 min-w-35">
                        <X className="h-4 w-4"/>
                        {t('payouts.action.cancel')}
                    </Button>)}
            </div>

            <PayDialog open={payOpen} onOpenChange={setPayOpen} payout={payout} onSuccess={() => {
            setPayOpen(false);
            refetch();
            queryClient.invalidateQueries({ queryKey: ['driver-payouts'] });
        }}/>

            <AddEntryDialog open={entryOpen} onOpenChange={setEntryOpen} payout={payout} onSuccess={() => {
            setEntryOpen(false);
            refetch();
            queryClient.invalidateQueries({ queryKey: ['driver-payouts'] });
        }}/>
        </div>);
}
function Detail({ label, value, mono, className, }) {
    return (<div className="space-y-0.5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className={`${mono ? 'tabular-nums font-medium' : ''} ${className ?? ''}`}>{value}</p>
        </div>);
}
function PayoutLedgerCard({ payout, disabled, onAddEntry, }) {
    const { t } = useTranslation('internal');
    return (<Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
                <div>
                    <CardTitle>{td(t, 'payouts.ledger_card.title')}</CardTitle>
                    <CardDescription>
                        {payout.entries.length === 0
            ? td(t, 'payouts.ledger_card.empty')
            : td(t, 'payouts.ledger_card.count', {
                count: payout.entries.length,
            })}
                    </CardDescription>
                </div>
                <Button size="sm" variant="outline" onClick={onAddEntry} disabled={disabled}>
                    <Plus className="h-4 w-4"/>
                    {td(t, 'payouts.ledger_card.add_entry')}
                </Button>
            </CardHeader>
            <CardContent>
                {payout.entries.length === 0 ? (<p className="py-6 text-center text-sm text-muted-foreground">—</p>) : (<ul className="divide-y">
                        {payout.entries.map((entry) => (<PayoutLedgerRow key={entry.publicId} entry={entry} currency={payout.currency}/>))}
                    </ul>)}
            </CardContent>
        </Card>);
}
function PayoutLedgerRow({ entry, currency }) {
    const { t } = useTranslation('internal');
    return (<li className="grid grid-cols-[auto_1fr_auto] items-start gap-3 py-3 text-sm">
            <Badge variant="outline" className={ENTRY_KIND_TONE[entry.kind]}>
                {td(t, `payouts.kind_option.${entry.kind}`)}
            </Badge>
            <div className="space-y-1 min-w-0">
                <p className="text-xs text-muted-foreground">
                    {formatDayTime(entry.occurredAt)}
                    {entry.paidByEmail ? ` · ${entry.paidByEmail}` : ''}
                </p>
                {entry.payoutMethod && (<p className="text-xs">
                        {td(t, `enum.payout_method.${entry.payoutMethod}`)}
                        {entry.payoutReference ? ` · ${entry.payoutReference}` : ''}
                    </p>)}
                {entry.notes && <p className="text-xs text-muted-foreground">{entry.notes}</p>}
                {entry.receiptImageUrl && (<a href={entry.receiptImageUrl} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                        {td(t, 'payouts.ledger_card.view_receipt')}
                    </a>)}
            </div>
            <p className={`tabular-nums font-medium ${ENTRY_KIND_TONE[entry.kind]}`}>
                {entry.signedAmount.startsWith('-') ? '' : '+'}
                {entry.signedAmount} {currency}
            </p>
        </li>);
}
function PayDialog({ open, onOpenChange, payout, onSuccess, }) {
    const { t } = useTranslation('internal');
    const form = useForm({
        defaultValues: {
            method: 'cash',
            reference: '',
            notes: '',
        },
    });
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => payDriverPayout({ publicId: payout.publicId, data }),
        onSuccess: () => {
            toast.success(t('payouts.toast.paid'));
            onSuccess();
        },
        onError: errorHandler,
    });
    const methodOptions = ['cash', 'aba_pay', 'wing', 'acleda_bank', 'other'].map((value) => ({
        value,
        label: td(t, `enum.payout_method.${value}`),
    }));
    return (<Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('payouts.pay_dialog.title', { name: payout.driverName })}</DialogTitle>
                    <DialogDescription>
                        {t('payouts.pay_dialog.desc', {
            currency: payout.currency,
            amount: payout.remaining,
            periodStart: formatDay(payout.periodStart),
            periodEnd: formatDay(payout.periodEnd),
        })}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                        <SelectField control={form.control} name="method" label={t('payouts.pay_dialog.method')} options={methodOptions} disabled={isPending}/>
                        <TextField control={form.control} name="reference" label={t('payouts.pay_dialog.reference')} disabled={isPending}/>
                        <TextAreaField control={form.control} name="notes" label={t('payouts.pay_dialog.notes')} disabled={isPending}/>
                    </form>
                </Form>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={form.handleSubmit((data) => mutate(data))} disabled={isPending}>
                        <Banknote className="h-4 w-4"/>
                        {t('payouts.pay_dialog.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
const KIND_OPTIONS = [
    { value: 'extra', cash: false },
    { value: 'deduction', cash: false },
    { value: 'advance', cash: true },
    { value: 'settlement', cash: true },
    { value: 'bonus', cash: true },
];
function AddEntryDialog({ open, onOpenChange, payout, onSuccess, }) {
    const { t } = useTranslation('internal');
    const form = useForm({
        defaultValues: {
            kind: 'extra',
            amount: '',
            method: 'cash',
            reference: '',
            notes: '',
            occurredAt: format(new Date(), 'yyyy-MM-dd'),
        },
    });
    const kind = useWatch({ name: 'kind', control: form.control });
    const isCash = KIND_OPTIONS.find((o) => o.value === kind)?.cash ?? false;
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => addLedgerEntry({
            publicId: payout.publicId,
            data: {
                ...data,
                method: isCash ? data.method : undefined,
                reference: isCash ? data.reference : undefined,
            },
        }),
        onSuccess: () => {
            toast.success(td(t, 'payouts.toast.entry_recorded'));
            form.reset();
            onSuccess();
        },
        onError: errorHandler,
    });
    return (<Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{td(t, 'payouts.ledger_card.dialog_title')}</DialogTitle>
                    <DialogDescription>{td(t, 'payouts.ledger_card.dialog_desc')}</DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                        <SelectField control={form.control} name="kind" label={td(t, 'payouts.ledger_card.field_kind')} options={KIND_OPTIONS.map((o) => ({
            value: o.value,
            label: td(t, `payouts.kind_option.${o.value}`),
        }))} disabled={isPending}/>
                        <DateField control={form.control} name="occurredAt" label={td(t, 'payouts.ledger.field_occurred_at')} disabled={isPending}/>
                        <TextField control={form.control} name="amount" label={td(t, 'payouts.ledger_card.field_amount', { currency: payout.currency })} placeholder={td(t, 'payouts.ledger_card.amount_placeholder')} disabled={isPending}/>
                        {isCash && (<SelectField control={form.control} name="method" label={td(t, 'payouts.ledger_card.field_payment_method')} options={['cash', 'aba_pay', 'wing', 'acleda_bank', 'other'].map((v) => ({
                value: v,
                label: td(t, `enum.payout_method.${v}`),
            }))} disabled={isPending}/>)}
                        {isCash && (<TextField control={form.control} name="reference" label={td(t, 'payouts.ledger_card.field_reference')} disabled={isPending}/>)}
                        <TextAreaField control={form.control} name="notes" label={td(t, 'payouts.ledger_card.field_notes')} disabled={isPending}/>
                    </form>
                </Form>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                        {td(t, 'payouts.ledger_card.cancel')}
                    </Button>
                    <Button onClick={form.handleSubmit((data) => mutate(data))} disabled={isPending}>
                        {td(t, 'payouts.ledger_card.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
